/**
 * Template-level preview — helps Legal visualise clause sequence, headings
 * and wording without needing to attach a party or issue a real NDA.
 *
 * Shares the print stylesheet with `NDAPrint.jsx` for visual parity.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { REEDOS_PRINT_STYLES as printStyles } from "../../lib/reedosPrintStyles";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function NDATemplatePreview() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tpl, setTpl] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/billing/nda-template/${id}`);
                setTpl(data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load template preview"));
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) return <div className="p-8 text-sm text-slate-500">Loading template preview…</div>;
    if (!tpl) return null;

    const clauses = (tpl.clauses || []).filter((c) => c.is_included !== false);
    const stateBadge = tpl.status === "Retired"
        ? "text-slate-600 bg-slate-100 border-slate-200"
        : tpl.status === "Draft"
        ? "text-amber-800 bg-amber-50 border-amber-200"
        : "text-emerald-800 bg-emerald-50 border-emerald-200";

    return (
        <div className="bg-white min-h-screen" data-testid="nda-template-preview-root">
            <style>{printStyles}</style>
            <style>{`
                .nda-doc { font-family: 'Georgia', 'Times New Roman', serif; color: #1f2937; padding: 32px 40px; max-width: 820px; margin: 0 auto; }
                .nda-doc h1 { font-size: 24px; letter-spacing: 0.6px; margin-bottom: 4px; text-transform: uppercase; text-align: center; }
                .nda-doc h2 { font-size: 13px; letter-spacing: 3px; text-align: center; color: #475569; margin-bottom: 28px; font-weight: 400; text-transform: uppercase; }
                .nda-doc .cover { border-top: 3px solid #0f172a; border-bottom: 1px solid #cbd5e1; padding: 20px 0; margin-bottom: 24px; text-align: center; }
                .nda-doc .clause { margin-bottom: 14px; }
                .nda-doc .clause .heading { font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; color: #0f172a; }
                .nda-doc .clause .body { font-size: 12px; line-height: 1.55; text-align: justify; color: #1e293b; white-space: pre-line; }
                @media print { .no-print { display: none !important; } }
            `}</style>
            <div className="no-print p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Button variant="outline" onClick={() => navigate(`/admin/nda-templates/${tpl.id}`)} className="gap-1.5" data-testid="nda-template-preview-back">
                        <ArrowLeft size={14} /> Back to editor
                    </Button>
                    <div className="text-xs text-slate-600">
                        <span className="font-mono font-semibold">{tpl.template_code}</span> v{tpl.version}
                        <span className={`ml-2 inline-block text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${stateBadge}`}>{tpl.status}</span>
                        <span className="ml-2 text-slate-500">Template-level preview (no party bound)</span>
                    </div>
                </div>
                <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800 text-white text-xs gap-1.5" data-testid="nda-template-preview-print">
                    <Printer size={14} /> Print
                </Button>
            </div>

            <div className="nda-doc" data-testid="nda-template-preview-doc">
                <div className="cover">
                    <h1>Non-Disclosure Agreement</h1>
                    <h2>{tpl.nda_type} NDA · {tpl.template_code} v{tpl.version}</h2>
                    <div style={{ fontSize: 12, color: "#475569" }}>
                        Template-level clause preview.  Party, dates and Annexure A are populated only when an NDA is issued from this template.
                    </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, color: "#475569", marginBottom: 4 }}>Default Purpose</div>
                    <div style={{ fontSize: 12, color: "#1e293b" }}>{tpl.default_purpose || "—"}</div>
                </div>

                <div data-testid="nda-template-preview-clauses">
                    {clauses.map((c, idx) => (
                        <div key={idx} className="clause">
                            <div className="heading">{idx + 1}. {c.heading}</div>
                            <div className="body">{c.body}</div>
                        </div>
                    ))}
                    {clauses.length === 0 && (
                        <div style={{ textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
                            No clauses included in this template.
                        </div>
                    )}
                </div>

                <div style={{ fontSize: 10, color: "#78350f", background: "#fefce8", borderLeft: "3px solid #f59e0b", padding: "10px 12px", marginTop: 24 }}>
                    This is a template preview.  Generated NDAs will additionally carry the party snapshot, dates, subject, disclaimer and Annexure A of confidential items.  The signed NDA remains a pre-commercial confidentiality document — no licence, production usage, resale, redistribution, sublicensing, or AI/ML training right is granted merely by signing.
                </div>
            </div>
        </div>
    );
}
